import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["checkbox", "countNumber", "applyButton", "modal", "searchInput", "loader", "selectedSection", "selectedList", "noSelectionMessage", "continentSection", "continentGrid", "hiddenInput", "continentToggle"]
  static values = {
    sessionCountryIds: Array,
    prefilledCountryId: Number,
    searchUrl: String
  }

  connect() {
    this.checkedCountryIds = new Set()
    this.committedSelectedIds = new Set()
    this.searchTimeout = null
    this.initializeCheckedIds()
    // Move initially selected items to top
    this.checkboxTargets.forEach(checkbox => {
      if (checkbox.checked) {
        this.moveSelectedToTop(checkbox)
      }
    })
    this.updateCount()
    this.updateSelectionView()
    if (!this.modalTarget.classList.contains('hidden')) {
      document.documentElement.classList.add('overflow-hidden')
      document.body.classList.add('overflow-hidden')
    }
  }

  disconnect() {
    document.documentElement.classList.remove('overflow-hidden')
    document.body.classList.remove('overflow-hidden')
  }

  initializeCheckedIds() {
    this.checkboxTargets.forEach(checkbox => {
      if (checkbox.checked) {
        this.checkedCountryIds.add(checkbox.value)
        this.committedSelectedIds.add(checkbox.value)
      }
    })
  }

  updateCount(event) {
    if (event && event.target) {
      const checkbox = event.target
      if (checkbox.checked) {
        this.checkedCountryIds.add(checkbox.value)
        this.moveSelectedToTop(checkbox)
      } else {
        this.checkedCountryIds.delete(checkbox.value)
        this.moveUnselectedBack(checkbox)
      }
    } else {
      // Re-sync everything (fallback)
      this.checkboxTargets.forEach(checkbox => {
        if (checkbox.checked) {
          this.checkedCountryIds.add(checkbox.value)
          // Ensure it's in the top list if not already
          if (!this.selectedListTarget.contains(checkbox.closest('label'))) {
            this.moveSelectedToTop(checkbox)
          }
        }
      })
    }

    this.updateSelectionView()
    this.updateContinentsVisibility()

    const count = this.checkedCountryIds.size
    this.sessionCountryIdsValue = Array.from(this.checkedCountryIds).map(id => parseInt(id))

    if (this.hasCountNumberTarget) {
      this.countNumberTarget.textContent = count
    }

    if (this.hasApplyButtonTarget) {
      this.applyButtonTarget.disabled = count === 0
      this.applyButtonTarget.title = count === 0 ? "Please select at least one country" : ""
    }

    this.updateAllContinentToggles()
  }

  selectAll() {
    this.checkboxTargets.forEach(checkbox => {
      if (!checkbox.checked) {
        checkbox.checked = true
        this.checkedCountryIds.add(checkbox.value)
        this.moveSelectedToTop(checkbox)
      }
    })
    this.updateCount()
  }

  selectNone() {
    this.checkboxTargets.forEach(checkbox => {
      if (checkbox.checked) {
        checkbox.checked = false
        this.moveUnselectedBack(checkbox)
      }
    })
    this.checkedCountryIds.clear()
    this.updateCount()
  }

  deselectAllSelected() {
    this.selectNone()
  }

  toggleContinent(event) {
    const continent = event.currentTarget.dataset.continent
    const continentCheckboxes = this.checkboxTargets.filter(cb => cb.dataset.continent === continent)
    const allSelected = continentCheckboxes.length > 0 && continentCheckboxes.every(cb => cb.checked)

    if (allSelected) {
      continentCheckboxes.forEach(checkbox => {
        checkbox.checked = false
        this.checkedCountryIds.delete(checkbox.value)
        this.moveUnselectedBack(checkbox)
      })
    } else {
      continentCheckboxes.forEach(checkbox => {
        if (!checkbox.checked) {
          checkbox.checked = true
          this.checkedCountryIds.add(checkbox.value)
          this.moveSelectedToTop(checkbox)
        }
      })
    }
    this.updateCount()
  }

  updateAllContinentToggles() {
    this.continentToggleTargets.forEach(toggle => {
      const continent = toggle.dataset.continent
      const checkboxes = this.checkboxTargets.filter(cb => cb.dataset.continent === continent)
      const allSelected = checkboxes.length > 0 && checkboxes.every(cb => cb.checked)
      toggle.textContent = allSelected ? 'Clear selection' : 'Select all in this continent'
    })
  }

  validate(event) {
    if (this.checkedCountryIds.size === 0) {
      event.preventDefault()
      alert("Please select at least one country")
      return false
    }

    const form = event.target
    this.hiddenInputTargets.forEach(input => input.remove())

    Array.from(this.checkedCountryIds).forEach(id => {
      const hiddenInput = document.createElement('input')
      hiddenInput.type = 'hidden'
      hiddenInput.name = 'country_ids[]'
      hiddenInput.value = id
      hiddenInput.dataset.countriesModalTarget = "hiddenInput"
      form.appendChild(hiddenInput)
    })

    return true
  }

  cancel(event) {
    this.checkedCountryIds.clear()

    this.checkboxTargets.forEach(checkbox => {
      const snapshot = this.committedSelectedIds
      const shouldBeChecked = snapshot.has(checkbox.value)

      if (checkbox.checked !== shouldBeChecked) {
        checkbox.checked = shouldBeChecked
      }

      if (shouldBeChecked) {
        this.checkedCountryIds.add(checkbox.value)
        this.moveSelectedToTop(checkbox)
      } else {
        this.moveUnselectedBack(checkbox)
      }
    })

    this.updateCount()

    if (this.checkedCountryIds.size === 0) {
      event.preventDefault()
      alert("Please select at least one country")
      return false
    }
    this.hideModal()
  }

  showModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove('hidden')
      document.documentElement.classList.add('overflow-hidden')
      document.body.classList.add('overflow-hidden')
    }
  }

  hideModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.add('hidden')
      document.documentElement.classList.remove('overflow-hidden')
      document.body.classList.remove('overflow-hidden')
    }
  }

  search(event) {
    const searchTerm = event.target.value.trim()
    this.updateSelectionView()

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    this.searchTimeout = setTimeout(() => {
      if (this.hasLoaderTarget) {
        this.loaderTarget.classList.remove('hidden')
      }

      const params = new URLSearchParams({ q: searchTerm })
      Array.from(this.checkedCountryIds).forEach(id => {
        params.append('selected_ids[]', id)
      })

      fetch(`${this.searchUrlValue}?${params.toString()}`, {
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
        .then(response => response.text())
        .then(html => {
          Turbo.renderStreamMessage(html)
          setTimeout(() => {
            this.checkboxTargets.forEach(checkbox => {
              checkbox.checked = this.checkedCountryIds.has(checkbox.value)
              if (checkbox.checked) {
                this.moveSelectedToTop(checkbox)
              }
            })
            this.updateCount()
            this.updateSelectionView()
            if (this.hasLoaderTarget) {
              this.loaderTarget.classList.add('hidden')
            }
          }, 50)
        })
        .catch(error => {
          console.error('Search error:', error)
          if (this.hasLoaderTarget) {
            this.loaderTarget.classList.add('hidden')
          }
        })
    }, 300)
  }
  moveSelectedToTop(checkbox) {
    if (!this.hasSelectedListTarget) return

    const label = checkbox.closest('label')
    if (label && !this.selectedListTarget.contains(label)) {
      this.selectedListTarget.appendChild(label)
    }
  }

  moveUnselectedBack(checkbox) {
    const label = checkbox.closest('label')
    const continent = checkbox.dataset.continent

    // Find the original continent section
    const grid = this.continentGridTargets.find(grid => grid.dataset.continent === continent)
    if (grid && label) {
      if (grid) {
        // Sort alphabetically: Find the correct position to insert
        const labelName = label.dataset.countryName || label.textContent.trim().toLowerCase()
        const siblings = Array.from(grid.children)

        const nextSibling = siblings.find(sibling => {
          const siblingName = sibling.dataset.countryName || sibling.textContent.trim().toLowerCase()
          return siblingName > labelName
        })

        if (nextSibling) {
          grid.insertBefore(label, nextSibling)
        } else {
          grid.appendChild(label)
        }
      }
    }
  }

  updateSelectionView() {
    const hasSelection = this.checkedCountryIds.size > 0
    // Check if there are actually visible items in the selected list
    const hasVisibleSelection = this.hasSelectedListTarget && this.selectedListTarget.children.length > 0
    const hasSearchTerm = this.hasSearchInputTarget && this.searchInputTarget.value.trim().length > 0

    if (this.hasSelectedSectionTarget) {
      if (hasVisibleSelection) {
        this.selectedSectionTarget.classList.remove('hidden')
      } else {
        this.selectedSectionTarget.classList.add('hidden')
      }
    }

    if (this.hasNoSelectionMessageTarget) {
      if (hasSelection || hasSearchTerm) {
        this.noSelectionMessageTarget.classList.add('hidden')
      } else {
        this.noSelectionMessageTarget.classList.remove('hidden')
      }
    }
  }

  updateContinentsVisibility() {
    this.continentSectionTargets.forEach(section => {
      const continent = section.dataset.continent
      const hasCheckboxes = this.checkboxTargets.some(cb => cb.dataset.continent === continent)
      if (hasCheckboxes) {
        section.classList.remove('hidden')
      } else {
        section.classList.add('hidden')
      }
    })
  }
}

